import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { computedAsync } from '@vueuse/core';

import {
  useBrowserStore,
  usePaxStore,
  usePriceCalendarStore,
  useRouterStore,
  useGeolocationStore,
  useUserLocationStore,
  useGlobalCurrencyStore,
} from '@apps/main/new-desktop/core/stores';
import {
  CoreEventSource,
  SessionStorageKey,
} from '@apps/main/new-desktop/core/models';
import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { useConfigurationPanelStore } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useTripStore } from '@apps/main/new-desktop/core/stores/trip/tripStore';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import type { MarkerType } from '@apps/main/modules/maps/markerFormatters';

import { MyPositionType } from '@libs/trip-planner-interfaces';
import type { Pax } from '@libs/trip-planner-interfaces';
import { isExperimentAsync } from '@libs/feature-toggle';
import type { Route } from '@libs/maps';

export const useCarTripPageStore = defineStore('CarTripPageStore', () => {
  const paxStore = usePaxStore();
  const priceCalendarStore = usePriceCalendarStore();
  const routerStore = useRouterStore();
  const browserStore = useBrowserStore();
  const tripStore = useTripStore();
  const configurationPanelStore = useConfigurationPanelStore();
  const geolocationStore = useGeolocationStore();
  const mapStore = useMapStore();
  const userLocationStore = useUserLocationStore();
  const globalCurrencyStore = useGlobalCurrencyStore();

  // State
  const hasUserOpenedPaxTab = ref<boolean>(false);
  const isCalendarWithoutPricesInCarTripExperiment = computedAsync(
    () => isExperimentAsync('calendar_without_prices_in_car_trip'),
    false
  );
  const routeToDisplay = computed<Route>(() => {
    const routes = mapStore.mapProperties.route || [];
    const firstRoute = routes[0];
    const secondRoute = routes[1];
    const samePositionRoute =
      firstRoute &&
      secondRoute &&
      firstRoute.from.position.latitude === secondRoute.to.position.latitude &&
      firstRoute.from.position.longitude === secondRoute.to.position.longitude;
    if (samePositionRoute) {
      return [firstRoute];
    }
    return mapStore.mapProperties.route || [];
  });

  // Actions
  function updatePax(pax: Pax): void {
    if (pax.length) {
      paxStore.updatePax(pax, CoreEventSource.TripOverview);
    }
  }

  function setUserOpenedPaxTab(): void {
    hasUserOpenedPaxTab.value = true;
  }

  function goToTripPlannerOverview(): void {
    if (!hasUserOpenedPaxTab.value) {
      setUserOpenedPaxTab();
    }
    browserStore.setSessionStorageItem(
      SessionStorageKey.IsCarTripPlannerUser,
      'true'
    );
    routerStore.navigateToTripOverview();
    handleTabSwitch(TabKey.Itinerary);
  }

  function handleTabSwitch(tabKey: TabKey): void {
    if (tabKey === TabKey.Pax && !hasUserOpenedPaxTab.value) {
      setUserOpenedPaxTab();
    }
    configurationPanelStore.handleTabSwitch(tabKey);
  }

  function loadUserGeolocationToMapState(): void {
    if (geolocationStore.userGeolocation) {
      return;
    }

    geolocationStore.loadUserCoordinates().then(() => {
      const coordinates = geolocationStore.userGeolocation;
      if (coordinates) {
        if (!mapStore.startingLocation) {
          mapStore.setPointLocationFromPosition(
            coordinates,
            MyPositionType.Start
          );
        }

        if (!mapStore.endLocation) {
          mapStore.setPointLocationFromPosition(
            coordinates,
            MyPositionType.End
          );
        }
      }
    });
  }

  function initializeView(): void {
    loadUserGeolocationToMapState();
  }

  function selectUserPosition(
    autocompleteType: DestinationAutocompleteType,
    placeId?: string,
    markerFormatter?: MarkerType
  ): void {
    if (autocompleteType === AutoCompleteType.StartingPosition) {
      if (userLocationStore.isUserPositionSame) {
        userLocationStore.setUserEndLocation(placeId, markerFormatter);
      }
      userLocationStore.setUserStartLocation(placeId, markerFormatter);
    }

    if (autocompleteType === AutoCompleteType.EndingPosition) {
      userLocationStore.setUserEndLocation(placeId, markerFormatter);
    }
  }

  // Getters
  const dateToDisplay = computed<string | undefined>(() =>
    isCalendarWithoutPricesInCarTripExperiment.value
      ? priceCalendarStore.calendarTriggerText
      : priceCalendarStore.selectedMonthOption.label
  );

  const fromPrice = computed<string | undefined>(() => {
    if (!tripStore.selectedItineraryWithStaticInfo?.totalPrice) {
      return;
    }
    return globalCurrencyStore.formatMoney(
      tripStore.selectedItineraryWithStaticInfo.totalPrice.amount,
      tripStore.selectedItineraryWithStaticInfo.totalPrice.currency
    );
  });

  const paxesToDisplay = computed<string>(
    () => paxStore.getPaxesButtonSimpleText
  );

  const paxesAmount = computed<number>(() => paxStore.numberOfTravelers);

  return {
    // State
    hasUserOpenedPaxTab,
    routeToDisplay,
    // Actions
    updatePax,
    setUserOpenedPaxTab,
    goToTripPlannerOverview,
    loadUserGeolocationToMapState,
    initializeView,
    selectUserPosition,
    handleTabSwitch,

    // Getters
    fromPrice,
    paxesToDisplay,
    paxesAmount,
    dateToDisplay,
  };
});
